import { Link } from "react-router-dom";
import type { Article } from "@/mocks/articles";

interface ArticleCardProps {
  article: Article;
  variant?: "default" | "featured" | "compact";
}

export default function ArticleCard({ article, variant = "default" }: ArticleCardProps) {
  if (variant === "compact") {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="flex gap-3 p-3 rounded-lg hover:bg-background-100 transition-colors cursor-pointer group"
      >
        <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0">
          <img
            src={article.coverImageUrl}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
            {article.title}
          </h4>
          <div className="flex items-center gap-3 mt-1.5 text-xs text-foreground-500">
            <span>{article.publishDate}</span>
            <span><i className="ri-eye-line mr-0.5"></i>{article.readCount > 10000 ? `${(article.readCount / 10000).toFixed(1)}万` : article.readCount}</span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === "featured") {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="block group cursor-pointer"
      >
        <div className="relative rounded-xl overflow-hidden aspect-[4/3] mb-4">
          <img
            src={article.coverImageUrl}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent"></div>
          {article.isExclusive && (
            <span className="absolute top-3 left-3 px-2.5 py-1 bg-accent-500 text-background-50 text-xs font-label rounded-full">
              独家爆料
            </span>
          )}
          <div className="absolute bottom-0 left-0 right-0 p-5">
            <h3 className="text-lg font-heading font-semibold text-white line-clamp-2 mb-2">
              {article.title}
            </h3>
            <div className="flex items-center gap-3 text-xs text-white/80">
              <span>{article.publishDate}</span>
              <span><i className="ri-eye-line mr-0.5"></i>{article.readCount > 10000 ? `${(article.readCount / 10000).toFixed(1)}万` : article.readCount} 阅读</span>
              <span><i className="ri-chat-3-line mr-0.5"></i>{article.commentCount} 评论</span>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.slug}`}
      className="block group cursor-pointer"
    >
      <div className="rounded-xl overflow-hidden bg-background-50 border border-background-200 transition-all duration-300 hover:border-primary-200">
        <div className="relative aspect-[16/10] overflow-hidden">
          <img
            src={article.coverImageUrl}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
          {article.isExclusive && (
            <span className="absolute top-3 left-3 px-2 py-0.5 bg-accent-500 text-background-50 text-xs font-label rounded-full">
              独家
            </span>
          )}
          {article.isHot && (
            <span className="absolute top-3 right-3 px-2 py-0.5 bg-primary-500 text-background-50 text-xs font-label rounded-full flex items-center gap-0.5">
              <i className="ri-fire-line text-[10px]"></i>热门
            </span>
          )}
        </div>
        <div className="p-4">
          <span className="inline-block px-2 py-0.5 bg-secondary-100 text-secondary-700 text-xs font-label rounded mb-2">
            {article.tags[0]}
          </span>
          <h3 className="text-base font-heading font-semibold text-foreground-900 line-clamp-2 mb-2 group-hover:text-primary-600 transition-colors">
            {article.title}
          </h3>
          <p className="text-sm text-foreground-600 line-clamp-2 mb-3 leading-relaxed">
            {article.excerpt}
          </p>
          <div className="flex items-center justify-between text-xs text-foreground-500">
            <span>{article.publishDate}</span>
            <div className="flex items-center gap-3">
              <span><i className="ri-eye-line mr-0.5"></i>{article.readCount > 10000 ? `${(article.readCount / 10000).toFixed(1)}万` : article.readCount}</span>
              <span><i className="ri-chat-3-line mr-0.5"></i>{article.commentCount}</span>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}